import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAnalyticsOverview } from '../../../hooks/useAnalyticsOverview';
import type { KpiValue } from '../../../services/analyticsService';
import './OverviewTab.css';

interface OverviewTabProps {
  year: number;
}

type DeltaDirection = 'up' | 'down' | 'flat' | 'na';

interface Delta {
  direction: DeltaDirection;
  pct: number | null;
}

const UNKNOWN_SERVICE_TYPE = 'UNKNOWN';

function computeDelta(value: number, previous: number | null): Delta {
  if (previous === null || previous === 0) {
    return { direction: 'na', pct: null };
  }
  const pct = ((value - previous) / previous) * 100;
  if (pct > 0.5) return { direction: 'up', pct };
  if (pct < -0.5) return { direction: 'down', pct };
  return { direction: 'flat', pct };
}

const DELTA_SYMBOL: Record<DeltaDirection, string> = {
  up: '▲',
  down: '▼',
  flat: '≈',
  na: '',
};

const monthLabel = (locale: string, year: number, month: number): string =>
  new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(year, month - 1, 1));

const KpiCard: React.FC<{ label: string; kpi: KpiValue; numberFormatter: Intl.NumberFormat }> = ({
  label,
  kpi,
  numberFormatter,
}) => {
  const { t } = useTranslation();
  const delta = computeDelta(kpi.value, kpi.previous);

  return (
    <div className="analytics-kpi-card">
      <div className="analytics-kpi-label">{label}</div>
      <div className="analytics-kpi-value">{numberFormatter.format(kpi.value)}</div>
      <div className={`analytics-kpi-delta analytics-kpi-delta-${delta.direction}`}>
        {delta.direction === 'na' ? (
          t('analytics.notAvailable')
        ) : (
          <>
            {DELTA_SYMBOL[delta.direction]} {delta.pct !== null ? `${delta.pct > 0 ? '+' : ''}${delta.pct.toFixed(0)}%` : ''}
          </>
        )}
      </div>
    </div>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({ year }) => {
  const { t, i18n } = useTranslation();
  const { overview, loading, error } = useAnalyticsOverview(year);

  const numberFormatter = new Intl.NumberFormat(i18n.language);
  const hoursFormatter = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (loading) {
    return <p className="analytics-coming-soon">{t('app.loading')}</p>;
  }

  if (error || !overview) {
    return <p className="analytics-error">{t('analytics.loadError')}</p>;
  }

  const { kpis, reportsByMonth, reportsByYear, machinesByServiceType } = overview;

  const monthlyChartData = reportsByMonth.current.map((count, index) => ({
    month: monthLabel(i18n.language, year, index + 1),
    current: count,
    previous: reportsByMonth.previous ? reportsByMonth.previous[index] : undefined,
  }));

  const yearlyChartData = reportsByYear.map((row) => ({
    year: String(row.year),
    count: row.count,
    isSelected: row.year === year,
  }));

  const maxServiceTypeCount = Math.max(...machinesByServiceType.map((row) => row.count), 1);

  return (
    <div className="analytics-overview">
      <div className="analytics-kpi-cards">
        <KpiCard label={t('analytics.kpis.reports')} kpi={kpis.reports} numberFormatter={numberFormatter} />
        <KpiCard
          label={t('analytics.kpis.machinesServed')}
          kpi={kpis.machinesServed}
          numberFormatter={numberFormatter}
        />
        <KpiCard
          label={t('analytics.kpis.sparePartsUsed')}
          kpi={kpis.sparePartsUsed}
          numberFormatter={numberFormatter}
        />
        <KpiCard label={t('analytics.kpis.travelDays')} kpi={kpis.travelDays} numberFormatter={numberFormatter} />
        <KpiCard label={t('analytics.kpis.workHours')} kpi={kpis.workHours} numberFormatter={hoursFormatter} />
        <KpiCard label={t('analytics.kpis.travelHours')} kpi={kpis.travelHours} numberFormatter={hoursFormatter} />
        <KpiCard label={t('analytics.kpis.travelKm')} kpi={kpis.travelKm} numberFormatter={numberFormatter} />
      </div>

      <div className="analytics-panels-grid">
        <div className="analytics-panel">
          <h3>{t('analytics.reportsByMonthTitle', { year })}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="current" name={String(year)} fill="var(--color-info)" radius={[4, 4, 0, 0]} />
              {reportsByMonth.previous && (
                <Bar
                  dataKey="previous"
                  name={String(year - 1)}
                  fill="var(--color-secondary-light)"
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-panel">
          <h3>{t('analytics.reportsByYearTitle')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={yearlyChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {yearlyChartData.map((entry) => (
                  <Cell
                    key={entry.year}
                    fill={entry.isSelected ? 'var(--color-warning)' : 'var(--color-info)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-panel">
        <h3>{t('analytics.machinesByServiceTypeTitle')}</h3>
        <div className="analytics-hbar-list">
          {machinesByServiceType.map((row) => (
            <div className="analytics-hbar-row" key={row.type}>
              <span className="analytics-hbar-label">
                {row.type === UNKNOWN_SERVICE_TYPE
                  ? t('analytics.notClassified')
                  : t(`analytics.serviceTypes.${row.type}`, { defaultValue: row.type })}
              </span>
              <div className="analytics-hbar-track">
                <div
                  className={`analytics-hbar-fill${row.type === UNKNOWN_SERVICE_TYPE ? ' analytics-hbar-fill-muted' : ''}`}
                  style={{ width: `${(row.count / maxServiceTypeCount) * 100}%` }}
                />
              </div>
              <span className="analytics-hbar-count">{numberFormatter.format(row.count)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
